import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { classNames } from '@framework/utils';
import type { PredictiveViewModelTag } from '@view-model/domain/view-model/ViewModelTagOperation';
import { ViewModelTagOperation } from '@view-model/domain/view-model/ViewModelTagOperation';
import { useCallback, useRef, useState } from 'react';
import { PredictiveViewModelTagList } from './PredictiveViewModelTagList';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { NewViewModelTagInput } from './NewViewModelTagInput';
import { toast } from 'react-hot-toast';
import type { UserId } from 'balus-common';
import { useTranslation } from 'react-i18next';
type Props = {
  isOpen: boolean;
  popupRef: React.RefObject<HTMLDivElement | null>;
  currentUserId: UserId;
  viewModel: ViewModelEntity;
  onCloseSearchWindow: () => void;
};

const WaitMsUntilPopupJudgeNotToClose = 100;

export const NewViewModelTagSearchWindow: React.FC<Props> = ({
  isOpen,
  popupRef,
  currentUserId,
  viewModel,
  onCloseSearchWindow,
}: Props) => {
  const { t } = useTranslation();
  const {
    isOpen: showList,
    popupRef: listRef,
    handleOpen: handleOpenList,
    handleClose: handleCloseList,
  } = usePopupRef<HTMLDivElement>(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const [keyword, setKeyword] = useState('');
  const [predictiveTagList, setPredictiveTagList] = useState<PredictiveViewModelTag[]>([]);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const newKeyword = e.target.value;
      setKeyword(newKeyword);

      // 入力されたキーワードに基づいてタグの候補を取得
      const newPredictiveTagList = await ViewModelTagOperation.predictiveList(
        viewModel.groupId,
        currentUserId,
        newKeyword
      );
      if (newPredictiveTagList.filter((tag) => tag.assignedViewModelCount > 0).length === 0) {
        handleCloseList();
      } else {
        handleOpenList();
      }
      setPredictiveTagList(newPredictiveTagList);
    },
    [currentUserId, handleCloseList, handleOpenList, viewModel.groupId]
  );

  const handleClearText = useCallback(() => {
    // usePopupRefのハンドラ処理が終わってからクリアを実行する
    //（クリアボタンが消えるとエリア外がクリックされたと判定され、ポップアップ自体も閉じてしまう）
    setTimeout(() => setKeyword(''), WaitMsUntilPopupJudgeNotToClose);
    inputRef.current?.focus();
  }, []);

  const terminateWindow = useCallback(() => {
    setKeyword('');
    handleCloseList();
    onCloseSearchWindow();
  }, [handleCloseList, onCloseSearchWindow]);

  const handleRegisterAndAssignTag = useCallback(async () => {
    try {
      const trimmedKeyword = keyword.trim();
      if (trimmedKeyword.length === 0) {
        toast.error(t('viewModel.tag.newTag.blankNameError'));
        return;
      }
      if (trimmedKeyword.length > 10) {
        toast.error(t('viewModel.tag.newTag.tooLongError'));
        return;
      }

      await ViewModelTagOperation.assign(viewModel.groupId, viewModel.id, trimmedKeyword);
      toast.success(t('viewModel.tag.newTag.created'));
    } catch (e) {
      toast.error(t('common.message.unknownError'));
      console.error(e);
    }
    terminateWindow();
  }, [keyword, t, terminateWindow, viewModel.groupId, viewModel.id]);

  const handleAssignTag = useCallback(
    async (tagName: string) => {
      try {
        await ViewModelTagOperation.assign(viewModel.groupId, viewModel.id, tagName);
      } catch (e) {
        toast.error(t('common.message.unknownError'));
        console.error(e);
      }
      terminateWindow();
    },
    [t, terminateWindow, viewModel.groupId, viewModel.id]
  );

  return (
    <>
      <div
        className={classNames(
          'pointer-events-auto absolute left-22 top-18 z-50 mt-2 flex h-[41px] w-100 rounded-lg bg-white p-1 shadow-xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute flex-none text-gray-600"
          style={{ top: '12px', left: '8px' }}
        />
        <div className="flex w-72 min-w-0">
          <NewViewModelTagInput keyword={keyword} onChange={handleChange} ref={inputRef} />
        </div>
        <div className="flex w-8 items-center justify-center gap-4 rounded-r-md bg-gray-200 pr-2">
          <button
            className="pointer-events-auto flex size-4 items-center justify-center rounded-full bg-gray-400 p-3 focus:outline-none"
            onClick={handleClearText}
          >
            <FontAwesomeIcon icon={faTimes} style={{ pointerEvents: 'none' }} />
          </button>
        </div>
        <div className="py-1 pl-1">
          <button
            className={classNames(
              'h-6 flex-none rounded-md px-2 text-xs text-white focus:outline-none',
              keyword === '' ? 'bg-gray-500' : 'bg-gray-600'
            )}
            onClick={handleRegisterAndAssignTag}
            disabled={keyword === ''}
          >
            {t('viewModel.tag.newTag.register')}
          </button>
        </div>
      </div>
      <div className="relative">
        <div
          className={classNames(
            'absolute left-6 top-10 z-50 mt-2 w-72 bg-white p-1 shadow-xl',
            showList ? 'block' : 'hidden'
          )}
          ref={listRef}
        >
          <PredictiveViewModelTagList predictiveTagList={predictiveTagList} onAssignTag={handleAssignTag} />
        </div>
      </div>
    </>
  );
};
